import type { ReactNode } from "react";
import { usePermissions } from "../../hooks/usePermissions";
import type { Permission } from "../../types/permissions";

interface RequirePermissionProps {
  /** Single permission to require. Mutually exclusive with `anyOf` / `allOf`. */
  permission?: Permission;
  /** Show children if the user has ANY of these permissions. */
  anyOf?: Permission[];
  /** Show children if the user has ALL of these permissions. */
  allOf?: Permission[];
  /** What to render if the check fails. Defaults to nothing. */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Renders `children` only when the current user satisfies the permission check.
 * Otherwise renders `fallback` (or nothing).
 *
 * Use for nav items, buttons, sections, modals — anywhere a UI element should
 * disappear for users who lack the underlying capability.
 *
 * For route-level gating, wrap the route element:
 *   <Route path="/projects/new" element={
 *     <RequirePermission permission={Permissions.PROJECT_CREATE} fallback={<Navigate to="/" />}>
 *       <NewProjectPage />
 *     </RequirePermission>
 *   } />
 */
export function RequirePermission({
  permission,
  anyOf,
  allOf,
  fallback = null,
  children,
}: RequirePermissionProps) {
  const { hasPermission, hasAny, hasAll } = usePermissions();

  let allowed = true;
  if (permission) allowed = hasPermission(permission);
  else if (anyOf && anyOf.length > 0) allowed = hasAny(...anyOf);
  else if (allOf && allOf.length > 0) allowed = hasAll(...allOf);

  return <>{allowed ? children : fallback}</>;
}
